import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Image as ImageIcon, Trash2, Loader2, CloudUpload } from 'lucide-react';
import { Habit, CheckInLog } from '../types';
import { format } from 'date-fns';
import { uploadCheckInImage, getCurrentUser, getCheckInImageUrl } from '../services/supabaseClient';
import { compressImage } from '../utils/imageCompression';
import { generateUUID } from '../utils/uuid';

interface CheckInModalProps {
    habit: Habit;
    date: string;
    existingLog?: CheckInLog;
    onSave: (habitId: string, log: CheckInLog) => void;
    onClose: () => void;
    onToggleCheckIn?: (id: string, date: string) => void;
}

export const CheckInModal: React.FC<CheckInModalProps> = ({ habit, date, existingLog, onSave, onClose, onToggleCheckIn }) => {
    const [note, setNote] = useState(existingLog?.note || '');
    const [image, setImage] = useState<string | undefined>(existingLog?.image); // Stores path or base64
    const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined); // Stores displayable URL
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<string>(''); // 'compressing' | 'uploading' | ''
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Resolve image URL (Signed URL) on mount and when image changes
    useEffect(() => {
        let active = true;

        if (!image) {
            setPreviewUrl(undefined);
            return;
        }

        const loadUrl = async () => {
            const url = await getCheckInImageUrl(image);
            if (active) setPreviewUrl(url);
        };

        loadUrl();
        return () => { active = false; };
    }, [image]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const originalFile = e.target.files?.[0];
        if (originalFile) {
            setIsUploading(true);
            setUploadStatus('正在压缩...');

            try {
                // 1. Compress
                const file = await compressImage(originalFile, 200); // Limit to 200KB

                // 2. Upload
                setUploadStatus('正在上传...');
                const user = await getCurrentUser();
                let uploadedPath: string | undefined;

                if (user) {
                    const { path, error } = await uploadCheckInImage(file, user.id);
                    if (error) {
                        console.warn("Cloud upload failed, falling back to local:", error);
                        // Don't throw, just fall through to local
                        alert("图片上传云端失败，将仅保存在本地。原因: " + error.message);
                    } else {
                        uploadedPath = path;
                    }
                }

                if (uploadedPath) {
                    setImage(uploadedPath);
                } else {
                    // Fallback to Base64 (Local)
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setImage(reader.result as string);
                    };
                    reader.readAsDataURL(file);
                }
            } catch (err) {
                console.error("Image processing error", err);
                // Fallback safety
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImage(reader.result as string);
                };
                reader.readAsDataURL(originalFile);
            } finally {
                setIsUploading(false);
                setUploadStatus('');
            }
        }
    };

    const handleSave = () => {
        if (note.trim() || image) {
            const newLog: CheckInLog = {
                id: existingLog?.id || generateUUID(),
                date: date,
                note: note,
                image: image,
                timestamp: Date.now()
            };
            onSave(habit.id, newLog);
        }
        // Only toggle if not already checked in
        if (onToggleCheckIn && !habit.completedDates.includes(date)) {
            onToggleCheckIn(habit.id, date);
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50">
                    <div>
                        <h2 className="text-lg font-bold text-slate-800 dark:text-white">打卡心得</h2>
                        <p className="text-xs text-slate-500">{habit.title} · {format(new Date(date), 'yyyy年MM月dd日')}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors">
                        <X size={20} className="text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto no-scrollbar space-y-4">

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            今日心得
                        </label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="今天感觉如何？记录一下吧..."
                            className="w-full h-32 bg-slate-50 dark:bg-slate-800 border-none rounded-xl p-4 text-slate-800 dark:text-white focus:ring-2 focus:ring-primary-500 resize-none"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                            添加照片
                        </label>

                        {!image ? (
                            <div
                                onClick={() => !isUploading && fileInputRef.current?.click()}
                                className={`w-full h-40 border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${isUploading ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}
                            >
                                {isUploading ? (
                                    <div className="flex flex-col items-center gap-2">
                                        <Loader2 size={24} className="animate-spin text-primary-500" />
                                        <span className="text-xs">{uploadStatus || '处理中...'}</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="flex space-x-2 mb-2">
                                            <Camera size={24} />
                                            <CloudUpload size={24} />
                                        </div>
                                        <span className="text-sm">点击上传照片</span>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="relative rounded-xl overflow-hidden group">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Check-in" className="w-full h-auto max-h-60 object-cover" />
                                ) : (
                                    <div className="w-full h-40 flex items-center justify-center bg-slate-100 dark:bg-slate-800 animate-pulse">
                                        <Loader2 className="animate-spin text-slate-400" />
                                    </div>
                                )}

                                <button
                                    onClick={() => setImage(undefined)}
                                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                    <Trash2 size={16} />
                                </button>
                                {image && !image.startsWith('data:') && !image.startsWith('http') && (
                                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/50 text-white text-xs rounded-md backdrop-blur-sm">
                                        云端私密存储
                                    </div>
                                )}
                            </div>
                        )}
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            onChange={handleFileChange}
                            disabled={isUploading}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900">
                    <div className="flex gap-3">
                        <button
                            onClick={() => {
                                if (onToggleCheckIn && !habit.completedDates.includes(date)) {
                                    onToggleCheckIn(habit.id, date);
                                }
                                onClose();
                            }}
                            className="flex-1 py-3 text-slate-500 font-bold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors"
                        >
                            跳过
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isUploading}
                            className="flex-1 bg-slate-900 dark:bg-white dark:text-slate-900 text-white py-3 rounded-xl font-bold hover:bg-slate-800 transition-all active:scale-95 shadow-lg shadow-slate-200 dark:shadow-none disabled:opacity-50"
                        >
                            {isUploading ? '上传中...' : '保存记录'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};